import React from 'react';
import { ShieldCheck, ExternalLink, CheckCircle2, Info } from 'lucide-react';
import { useT } from '../lib/i18n';
import { useData } from '../contexts/DataContext';
import {
  getLicenceProvider,
  licenceCardTitle,
  registryButtonLabel,
  noRegistryNotice,
} from '../lib/licenceProvider';

/**
 * Canadian contractor licence verification.
 * Deep-links to the OFFICIAL public registry for the user's province
 * when one exists. When the province has no central public registry
 * (Manitoba, NB, NS, PE, NL, YT, NT, NU), shows an honest disclaimer instead.
 */
export function LicenceVerifyButtons({ rbqNumber, province }) {
  const { lang } = useT();
  const { profile, saveProfile } = useData();
  const trimmed = (rbqNumber || '').trim();
  const isVerified = !!profile?.rbq_verified;
  const prov = (province || profile?.province || 'QC').toUpperCase();
  const lp = getLicenceProvider(prov);

  const openRegistry = () => {
    if (!lp.registryUrl) return;
    window.open(lp.registryUrl, '_blank', 'noopener,noreferrer');
  };

  const toggleVerified = async () => {
    if (!profile) return;
    try {
      await saveProfile({ ...profile, rbq_verified: !isVerified });
    } catch (e) { console.warn('[licence] toggle verified failed:', e); }
  };

  const verifiedWord  = lang === 'fr' ? 'vérifiée' : 'verified';
  const verifiedShort = lang === 'fr' ? 'Licence vérifiée' : 'Licence verified';

  return (
    <div className="card p-4 border-2 border-[#E5DCC9]">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isVerified ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700'
        }`}>
          {isVerified ? <CheckCircle2 size={20} /> : <ShieldCheck size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-base">
            {licenceCardTitle(prov, lang)}
          </div>

          {/* Province WITH a public registry → show explainer + Open button */}
          {lp.hasRegistry && (
            <>
              <div className="text-xs text-gray-600 mt-1 leading-relaxed">
                {trimmed
                  ? (lang === 'fr'
                      ? `Cliquez pour vérifier votre numéro ${lp.short} ${trimmed} dans le registre officiel.`
                      : `Click to verify your ${lp.short} number ${trimmed} in the official registry.`)
                  : (lang === 'fr'
                      ? `Entrez votre numéro ${lp.short} dans le profil ci-dessus, puis cliquez pour le vérifier.`
                      : `Add your ${lp.short} number in the profile above, then click to verify.`)}
              </div>
              <button
                data-testid="verify-rbq"
                onClick={openRegistry}
                className="btn-outline gap-2 !min-h-[40px] !text-xs w-full mt-3"
              >
                <ExternalLink size={12} /> {registryButtonLabel(prov, lang)}
              </button>
            </>
          )}

          {/* Province WITHOUT a public registry → honest disclaimer, no button */}
          {!lp.hasRegistry && (
            <div
              data-testid="licence-no-registry-notice"
              className="mt-2 flex gap-2 p-3 rounded-lg bg-[#FAF7F2] border border-[#E5DCC9]"
            >
              <Info size={14} className="text-[#7A1F2B] shrink-0 mt-0.5" />
              <div className="text-xs text-gray-700 leading-relaxed">
                {noRegistryNotice(prov, lang)}
              </div>
            </div>
          )}

          {trimmed && (
            <label className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-[#FAF7F2] border border-[#E5DCC9] cursor-pointer">
              <input
                data-testid="rbq-verified-checkbox"
                type="checkbox"
                checked={isVerified}
                onChange={toggleVerified}
                className="mt-0.5 w-4 h-4 accent-emerald-600"
              />
              <span className="text-xs text-gray-700 leading-relaxed">
                {lang === 'fr'
                  ? <><b>J&apos;ai vérifié ma licence{lp.short !== 'Licence' ? ` ${lp.short}` : ''}.</b> Cocher cette case affichera le badge « Licence {verifiedWord} » sur les soumissions envoyées à vos clients.</>
                  : <><b>I have verified my {lp.short === 'Licence' ? 'contractor' : lp.short} licence.</b> Checking this displays a &ldquo;Licence verified&rdquo; badge on quotes sent to clients.</>}
              </span>
            </label>
          )}
          {isVerified && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200">
              <CheckCircle2 size={12} className="text-emerald-700" />
              <span className="text-[11px] font-bold text-emerald-800">
                {verifiedShort}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
